import { Button, Popover, List, Input, Modal } from "antd"
import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import ExpenseTag from "Components/expenseTag"
import { attachTagExpense } from "Shared/store/actions/expense/expenseActions"
import { createExpenseTag, deleteExpenseTag } from "Shared/store/actions/expenseTag/expenseTagActions"
import { useExpenseTags, useExpenseTagsInLoadingState } from "Shared/hooks/expenseTags.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { ExpenseTag as ExpenseTagModel, ExpenseTagColorEnum } from "@finway-group/shared/lib/models"
import { ExpenseTagService, NotificationService } from "Shared/services"
import { Trash2 as TrashIcon } from "react-feather"
import Icon from "Components/icon"
import { useCompany } from "Shared/hooks/company.hooks"
import { isEmptyString } from "Shared/utils/helpers"

interface ExpenseTagManagerPopoverInterface {
    expenseId: string
    existingTags: ExpenseTagModel[]
}

enum ExpenseTagStepEnum {
    SELECT_TAG_NAME,
    SELECT_TAG_COLOR,
}

const ExpenseTagManagerPopover: React.FC<ExpenseTagManagerPopoverInterface> = ({ expenseId, existingTags }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [currentStep, setCurrentStep] = useState(ExpenseTagStepEnum.SELECT_TAG_NAME)
    const [tagName, setTagName] = useState("")
    const [isTagCreationDisabled, setIsTagCreationDisabled] = useState(false)
    const { expenseTagsLoading, addTagInLoading, removeTagFromLoading } = useExpenseTagsInLoadingState()

    const loggedInUser = useLoggedInEmployeeProfile()
    const expenseTags = useExpenseTags()
    const { editingTagsEnabled } = useCompany()

    const onTagSelected = async (expenseTagId: string) => {
        try {
            addTagInLoading(expenseTagId)
            await dispatch(attachTagExpense(expenseTagId, expenseId))
            setTagName("")
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.attach.title"))
        } finally {
            removeTagFromLoading(expenseTagId)
        }
    }

    const onTagCreate = async (tagColor: ExpenseTagColorEnum) => {
        try {
            setIsTagCreationDisabled(true)
            await dispatch(createExpenseTag({ tagName, tagColor }, expenseId))
            setTagName("")
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.create.title"))
        } finally {
            setIsTagCreationDisabled(false)
            setCurrentStep(ExpenseTagStepEnum.SELECT_TAG_NAME)
        }
    }

    const onTagDelete = async (expenseTagId: string, tagName: string) => {
        try {
            const data = await ExpenseTagService.getTotalTagAttachCount(expenseTagId)

            Modal.confirm({
                title: t("confirm:expenseTag.delete.title"),
                type: "warning",
                content: t("confirm:expenseTag.delete.message", { expenseTagName: tagName, count: data.totalCount }),
                cancelText: t("confirm:expenseTag.delete.cancel"),
                okText: t("confirm:expenseTag.delete.confirm"),
                async onOk() {
                    try {
                        await dispatch(deleteExpenseTag(expenseTagId))
                    } catch (err) {
                        NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.delete.title"))
                    }
                },
            })
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.delete.title"))
        }
    }

    let overlay

    switch (currentStep) {
        case ExpenseTagStepEnum.SELECT_TAG_NAME:
            const canEditTags = loggedInUser.activeCompanyProfile?.isAdmin || editingTagsEnabled
            const hasSearchedTag = !isEmptyString(tagName.trim())
            const isTagAlreadyCreated = expenseTags.find((expenseTag) => expenseTag.tagName === tagName)

            const availableTags = expenseTags.filter((tag) => !existingTags.find((existingTag) => existingTag._id == tag._id))
            const filteredTags = hasSearchedTag ? availableTags.filter((tag) => tag.tagName.toLowerCase().startsWith(tagName.toLowerCase())) : availableTags

            let container
            if (filteredTags.length == 0 && !hasSearchedTag) {
                container = <span className="text-gray-400">{t("info:expense_tag.not_found")}</span>
            } else if (filteredTags.length == 0 && hasSearchedTag) {
                container = isTagAlreadyCreated ? (
                    <span className="text-gray-400">{t("info:expense_tag.added_already")}</span>
                ) : (
                    !canEditTags && <span className="text-gray-400">{t("info:expense_tag.not_found")}</span>
                )
            } else {
                container = (
                    <List className="h-64 max-h-200 overflow-y-scroll">
                        {filteredTags.map((tag) => (
                            <List.Item
                                key={tag._id}
                                className="flex justify-between h-40 cursor-pointer border-none hover:bg-body custom-group"
                                onClick={() => onTagSelected(tag._id)}
                            >
                                <ExpenseTag className="h-30 cursor-pointer" color={tag.tagColor} isLoading={expenseTagsLoading.includes(tag._id)}>
                                    {tag.tagName}
                                </ExpenseTag>
                                {canEditTags && (
                                    <TrashIcon
                                        className="mr-3 hidden-child"
                                        color="#A1B1C1"
                                        size="15"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onTagDelete(tag._id, tag.tagName)
                                        }}
                                    />
                                )}
                            </List.Item>
                        ))}
                    </List>
                )
            }

            overlay = (
                <div className="flex flex-col">
                    <Input className="my-6" maxLength={30} value={tagName} onChange={(e) => setTagName(e.target.value)} />
                    {container}
                    {!isTagAlreadyCreated && hasSearchedTag && canEditTags && (
                        <Button className="mt-8 btn-default text-text" onClick={() => setCurrentStep(ExpenseTagStepEnum.SELECT_TAG_COLOR)}>
                            {t("action:expense_tag.create")}
                            <span className="text-primary">&nbsp;&ldquo;{tagName}&ldquo;</span>
                        </Button>
                    )}
                </div>
            )
            break
        case ExpenseTagStepEnum.SELECT_TAG_COLOR:
            overlay = (
                <div className="flex flex-row justify-between">
                    {Object.keys(ExpenseTagColorEnum)
                        .filter((key) => !isNaN(Number(key)))
                        .map((color) => (
                            <ExpenseTag
                                className="rounded-full h-30 w-30 cursor-pointer"
                                key={color}
                                onClick={() => !isTagCreationDisabled && onTagCreate(Number(color))}
                                color={Number(color)}
                            />
                        ))}
                </div>
            )
            break
        default:
            overlay = <></>
    }

    return (
        <Popover
            className="flex items-center"
            title={<span className="text-primary text-base block my-4">Tags</span>}
            content={overlay}
            overlayStyle={{ minWidth: "215px", zIndex: 10 }}
            z-index={10}
            trigger="click"
            placement="bottomRight"
            onVisibleChange={() => (currentStep > 1 ? setCurrentStep(ExpenseTagStepEnum.SELECT_TAG_NAME) : null)}
        >
            <Button
                className="m-5 rounded-full bg-transparent border-solid font-normal text-gray-800  border-gray-800 border-1"
                icon={<Icon className="-ml-2" icon="add_expense_tag" />}
            >
                {existingTags.length > 0 ? null : <span className="ml-3">{t("action:expense_tag.add")} </span>}
            </Button>
        </Popover>
    )
}

export default ExpenseTagManagerPopover
